import { useEffect, useState, type FC } from "react";
import { useTranslation } from "react-i18next";
import CheckRounded from "@mui/icons-material/CheckRounded";
import ContentCopyRounded from "@mui/icons-material/ContentCopyRounded";
import ImageOutlined from "@mui/icons-material/ImageOutlined";
import OpenInNewRounded from "@mui/icons-material/OpenInNewRounded";
import {
  Box,
  Button,
  Card,
  Chip,
  IconButton,
  Link,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { formatCurrency } from "@/partner/shared/utils";
import { useAssistantNavigate } from "../model/assistantNavigation";
import { ENTITY_STATUS_KEYS, ENTITY_TYPE_KEYS } from "../model/labels";
import { isCabinetPath, isHttpUrl } from "../model/safeUrl";
import type { EntityCard, EntityStatus } from "../model/types";

const STATUS_COLORS: Record<EntityStatus, "success" | "info" | "default"> = {
  PUBLISHED: "success",
  SCHEDULED: "info",
  DRAFT: "default",
  FINISHED: "default",
};

const THUMB_SX = {
  width: { xs: 88, sm: 112 },
  aspectRatio: "16 / 9",
  borderRadius: 1,
  flexShrink: 0,
  alignSelf: "flex-start",
} as const;

const Thumbnail: FC<{ url: string | null }> = ({ url }) => {
  const [broken, setBroken] = useState(false);
  if (!isHttpUrl(url) || broken) {
    return (
      <Box
        sx={{
          ...THUMB_SX,
          bgcolor: "action.hover",
          color: "text.disabled",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <ImageOutlined fontSize="small" />
      </Box>
    );
  }
  return (
    <Box
      component="img"
      src={url}
      alt=""
      loading="lazy"
      onError={() => setBroken(true)}
      sx={{ ...THUMB_SX, objectFit: "cover", bgcolor: "action.hover" }}
    />
  );
};

/**
 * Карточка результата или предпросмотра (п. 4 спеки): обложка, название,
 * дата, цена, статус, ссылки. Кабинет открывается на месте через роутер и
 * сворачивает панель, витрина в новой вкладке.
 */
export const EntityCardView: FC<{ card: EntityCard }> = ({ card }) => {
  const { t } = useTranslation();
  const openInCabinet = useAssistantNavigate();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const typeKey = ENTITY_TYPE_KEYS[card.entityType];
  const statusKey = card.status ? ENTITY_STATUS_KEYS[card.status] : undefined;
  const cabinetUrl =
    !card.preview && isCabinetPath(card.cabinetUrl) ? card.cabinetUrl : null;
  const siteUrl =
    !card.preview && isHttpUrl(card.siteUrl) ? card.siteUrl : null;
  const copyUrl =
    !card.preview && isHttpUrl(card.copyUrl) ? card.copyUrl : null;

  const copy = () => {
    if (!copyUrl) return;
    navigator.clipboard
      ?.writeText(copyUrl)
      .then(() => setCopied(true))
      .catch(() => undefined);
  };

  return (
    <Card
      variant="outlined"
      sx={{
        display: "flex",
        gap: 1.5,
        p: 1.25,
        borderStyle: card.preview ? "dashed" : "solid",
        transition: (theme) =>
          theme.transitions.create(["border-color", "background-color"], {
            duration: theme.transitions.duration.shortest,
          }),
        ...(cabinetUrl && {
          "&:hover": { borderColor: "primary.main" },
        }),
      }}
    >
      <Thumbnail url={card.imageUrl} />
      <Stack sx={{ minWidth: 0, flex: 1 }} gap={0.25}>
        <Stack direction="row" alignItems="center" gap={0.75} minWidth={0}>
          {typeKey && (
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ textTransform: "uppercase", letterSpacing: 0.4 }}
            >
              {t(typeKey)}
            </Typography>
          )}
          {card.preview && (
            <Chip
              size="small"
              variant="outlined"
              label={t("partnerAssistant.card.preview")}
              sx={{ height: 20, fontSize: "0.6875rem" }}
            />
          )}
          {statusKey && card.status && (
            <Chip
              size="small"
              color={STATUS_COLORS[card.status]}
              variant={card.status === "PUBLISHED" ? "filled" : "outlined"}
              label={t(statusKey)}
              sx={{ height: 20, fontSize: "0.6875rem", ml: "auto" }}
            />
          )}
        </Stack>
        {cabinetUrl ? (
          <Link
            component="button"
            type="button"
            variant="subtitle2"
            color="text.primary"
            underline="hover"
            noWrap
            onClick={() => openInCabinet(cabinetUrl)}
            sx={{ textAlign: "left", display: "block", maxWidth: "100%" }}
          >
            {card.title}
          </Link>
        ) : (
          <Typography variant="subtitle2" noWrap>
            {card.title}
          </Typography>
        )}
        {(card.startAt || card.price || card.subtitle) && (
          <Stack
            direction="row"
            columnGap={1.5}
            rowGap={0}
            flexWrap="wrap"
            alignItems="baseline"
          >
            {card.startAt && (
              <Typography variant="body2" color="text.secondary">
                {t("partnerAssistant.card.startAt", { value: card.startAt })}
              </Typography>
            )}
            {card.price && (
              <Typography variant="body2" fontWeight={600}>
                {formatCurrency(card.price.value, card.price.currency)}
              </Typography>
            )}
            {card.subtitle && (
              <Typography variant="body2" color="text.secondary" noWrap>
                {card.subtitle}
              </Typography>
            )}
          </Stack>
        )}
        {(cabinetUrl || siteUrl || copyUrl) && (
          <Stack
            direction="row"
            alignItems="center"
            gap={0.5}
            flexWrap="wrap"
            sx={{ mt: 0.5, ml: -0.75 }}
          >
            {cabinetUrl && (
              <Button size="small" onClick={() => openInCabinet(cabinetUrl)}>
                {t("partnerAssistant.card.openCabinet")}
              </Button>
            )}
            {siteUrl && (
              <Button
                size="small"
                color="inherit"
                href={siteUrl}
                target="_blank"
                rel="noopener noreferrer"
                endIcon={<OpenInNewRounded sx={{ fontSize: 16 }} />}
                sx={{ color: "text.secondary" }}
              >
                {t("partnerAssistant.card.openSite")}
              </Button>
            )}
            {copyUrl && (
              <Tooltip
                title={
                  copied
                    ? t("partnerAssistant.card.copied")
                    : t("partnerAssistant.card.copyLink")
                }
              >
                <IconButton
                  size="small"
                  aria-label={t("partnerAssistant.card.copyLink")}
                  onClick={copy}
                  color={copied ? "success" : "default"}
                >
                  {copied ? (
                    <CheckRounded sx={{ fontSize: 18 }} />
                  ) : (
                    <ContentCopyRounded sx={{ fontSize: 18 }} />
                  )}
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        )}
      </Stack>
    </Card>
  );
};
